/**
 * V2 PaSurveyHomePage
 * Entry page for patient survey with terms acceptance
 */

import { memo, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import produce from 'immer';
import { isEmpty } from 'lodash';
import { useStaticLocales, SystemLocaleCodes, SystemLocaleKeys } from '../../../../admin/hooks/locales/use-static-locale';
import { usePatientLocale } from '../../../hooks/use-patient-locale';
import Footer from '../../../../app/Footer';
import PaSurveyHeader from '../../../../app/PaSurveyHeader';
import LoadingAnim from '../../../../components/LoadingAnim';
import config from '../../../../config';
import SurveyContainer from './SurveyContainer';
import il1 from '../../../../patient/survey/assets/DoctorPatient.svg';
import il2 from '../../../../patient/survey/assets/HealthcareWorker.svg';
import { usePatientBasePath } from '../../../../app/route-utils';
import { usePatientTransData } from '../../../hooks/use-patient-trans-data';
import { useTranslations } from '../../../../patient/hooks/use-translations';
import { sanitizeSurveyTranslationForLocale } from './lib/sanitize-survey-translation';
import { mapDefaultLanguageToLocaleKey } from './locale';
import { getLocaleCode } from '../../organization/configurations/language/constants';
import {
  buildSubmissionLockStorageKey,
  isIntegratedVisit,
  isSubmissionEditLocked,
} from './submissionLock';

/**
 * Page Content Component
 * Uses Container.useContainer() to access all state and functions
 */
function PaSurveyHomePageContent() {
  const {
    localeKey: patientLocaleKey,
    localeCode: patientLocaleCode,
    userLocaleKey,
  } = usePatientLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const patientBasePath = usePatientBasePath();

  // Get all data from Container - no props drilling!
  const {
    isLoading,
    reportState,
    surveyState,
    survey,
    visit,
    dataState,
    defaultLanguage,
    setReportState,
    navigation,
  } = SurveyContainer.useContainer();

  const defaultLocaleKey = mapDefaultLanguageToLocaleKey(defaultLanguage);
  const effectiveLocaleKey = userLocaleKey || defaultLocaleKey || patientLocaleKey;
  const effectiveLocaleCode = getLocaleCode(effectiveLocaleKey) || patientLocaleCode;

  const { slt, sltStr } = useStaticLocales({ localeCode: effectiveLocaleCode as SystemLocaleCodes });
  const [, { t: tEffective }] = useTranslations({ locale: effectiveLocaleKey });
  const [, { t: tDefault }] = useTranslations({ locale: defaultLocaleKey });
  const [, { t: tPatient }] = useTranslations({ locale: patientLocaleKey });

  const { params, baseUrl, startSurvey } = navigation;
  const { orgId, shortId, referralMessageId } = params;

  // Get query params from URL
  const searchParams = new URLSearchParams(location.search);
  const aid = searchParams.get('aid');
  let customParamValue = searchParams.get('custom1');
  let anonymizedId = searchParams.get('aid');

  if (customParamValue) {
    const base64Pattern = /^[A-Za-z0-9+/=]+$/;
    if (base64Pattern.test(customParamValue) && customParamValue.length % 4 === 0) {
      try {
        customParamValue = atob(customParamValue);
      } catch (error) {
        // ignore decode error
      }
    }
  }

  if (anonymizedId) {
    const base64Pattern = /^[A-Za-z0-9+/=]+$/;
    if (base64Pattern.test(anonymizedId) && anonymizedId.length % 4 === 0) {
      try {
        anonymizedId = atob(anonymizedId);
      } catch (error) {
        // ignore decode error
      }
    }
  }

  const isValid = reportState.acceptedTerms;
  const eid = searchParams.get('eid');
  const isIntegrated = isIntegratedVisit({ aid, eid, referralMessageId });
  const submissionLockStorageKey = useMemo(
    () =>
      buildSubmissionLockStorageKey({
        shortId,
        aid,
        eid,
        referralMessageId,
      }),
    [shortId, aid, eid, referralMessageId],
  );
  const localSubmittedAt = useMemo(() => {
    try {
      return localStorage.getItem(submissionLockStorageKey);
    } catch (error) {
      console.warn('Failed to read submission lock timestamp:', error);
      return null;
    }
  }, [submissionLockStorageKey]);

  const isSubmissionLocked = useMemo(
    () =>
      isSubmissionEditLocked({
        visit,
        localSubmittedAt,
        isIntegrated,
      }),
    [visit, localSubmittedAt, isIntegrated],
  );

  useEffect(() => {
    if (!isSubmissionLocked) return;
    navigate(navigation.finishedUrl);
  }, [isSubmissionLocked, navigate, navigation.finishedUrl]);

  // Handle terms checkbox change with custom params storage
  const handleTermsChange = useCallback(
    (checked: boolean) => {
      setReportState((st) =>
        produce(st, (dr) => {
          dr.acceptedTerms = checked;

          // Store URL parameters into systemValueMap
          if (!dr?.systemValueMap) dr.systemValueMap = {};
          if (!isEmpty(customParamValue)) {
            dr.systemValueMap = {
              ...dr?.systemValueMap,
              custom1: customParamValue,
            };
          }
          if (!isEmpty(anonymizedId)) {
            dr.systemValueMap = {
              ...dr?.systemValueMap,
              anonymized_id: anonymizedId,
              lastName: anonymizedId,
            };
          }
        })
      );
    },
    [setReportState, customParamValue, anonymizedId]
  );

  // Handle start survey button click
  const handleStartSurvey = useCallback(async () => {
    if (!isValid) return;
    if (isSubmissionLocked) {
      navigate(navigation.finishedUrl);
      return;
    }

    // Check if survey is already completed
    if (visit?.status === 'done' || visit?.archived) {
      navigate(`${baseUrl}/terminated`);
      return;
    }

    // If no AID, start survey directly
    if (!aid) {
      startSurvey();
      return;
    }

    // With AID, check if patient details are available
    if (surveyState.patientDetails) {
      startSurvey();
    } else {
      // Redirect to Athena info check
      navigate(`${patientBasePath}/${orgId}/${shortId}/athena-check${location.search}`);
    }
  }, [
    isValid,
    isSubmissionLocked,
    visit?.status,
    visit?.archived,
    aid,
    surveyState.patientDetails,
    startSurvey,
    navigate,
    baseUrl,
    orgId,
    shortId,
    location.search,
    navigation.finishedUrl,
  ]);

  const firstPageKeys = survey?.metadata?.frontPageKeys || [];
  const [{ loadingTrans }] = usePatientTransData({
    keys: firstPageKeys,
    locale: effectiveLocaleKey,
  });
  const [{ loadingTrans: loadingTransDefault }] = usePatientTransData({
    keys: firstPageKeys,
    locale: defaultLocaleKey,
  });
  const [{ loadingTrans: loadingTransPatient }] = usePatientTransData({
    keys: firstPageKeys,
    locale: patientLocaleKey,
  });

  // Error state
  const errors = dataState?.errors;
  if (errors && errors.length > 0) {
    return (
      <div className='flex flex-1 w-full items-center justify-center flex-col gap-4 p-5'>
        <LoadingAnim className='h-full p-10 w-full' />
        <p className='text-center text-gray-500'>Initializing survey...</p>
        <button className='btn btn-ghost btn-sm mt-4' onClick={() => window.location.reload()}>
          Refresh
        </button>
      </div>
    );
  }

  // Loading state
  if (isLoading || loadingTrans || loadingTransDefault || loadingTransPatient || !survey) {
    return (
      <div className='flex flex-1 w-full'>
        <LoadingAnim className='h-full p-10 w-full' />
      </div>
    );
  }

  // Determine title and subtitle keys based on survey description
  const titleKeyAcep = 'survey-title-acep';
  const titleKeyYc = 'survey-title-yc';
  const titleKeyCommon = 'survey-title';

  let titleKey: SystemLocaleKeys = survey.desc?.includes('#yc') ? titleKeyYc : titleKeyCommon;
  titleKey = survey.desc?.includes('#acep') ? titleKeyAcep : titleKey;

  const subtitleKeyAcep = 'survey-subtitle-acep';
  const subtitleKeyCommon = 'survey-subtitle';

  const subtitleKey: SystemLocaleKeys = survey.desc?.includes('#acep')
    ? subtitleKeyAcep
    : subtitleKeyCommon;

  const customTextReplacements = {
    custom1: customParamValue || '',
    anonymized_id: anonymizedId || '',
  };
  const localeCandidates = [
    effectiveLocaleKey,
    patientLocaleKey,
    defaultLocaleKey,
    'english',
  ].filter(Boolean);
  const titleCandidates = [
    { text: tEffective('front-page-title', customTextReplacements, { fallbackValue: null }), locale: effectiveLocaleKey },
    { text: tPatient('front-page-title', customTextReplacements, { fallbackValue: null }), locale: patientLocaleKey },
    { text: tDefault('front-page-title', customTextReplacements, { fallbackValue: null }), locale: defaultLocaleKey },
  ];
  const subtitleCandidates = [
    { text: tEffective('front-page-desc', customTextReplacements, { fallbackValue: null }), locale: effectiveLocaleKey },
    { text: tPatient('front-page-desc', customTextReplacements, { fallbackValue: null }), locale: patientLocaleKey },
    { text: tDefault('front-page-desc', customTextReplacements, { fallbackValue: null }), locale: defaultLocaleKey },
  ];

  const titleLabel =
    titleCandidates
      .map((candidate) =>
        sanitizeSurveyTranslationForLocale(
          candidate.text,
          String(candidate.locale || localeCandidates[0] || 'english').toLowerCase(),
        ),
      )
      .find(Boolean) || null;
  const subtitleLabel =
    subtitleCandidates
      .map((candidate) =>
        sanitizeSurveyTranslationForLocale(
          candidate.text,
          String(candidate.locale || localeCandidates[0] || 'english').toLowerCase(),
        ),
      )
      .find(Boolean) || null;

  return (
    <div className='flex-col flex-1 w-full gap-5 px-5 pt-2'>
      <div className='flex-col flex-1 w-full gap-6'>
        <PaSurveyHeader />

        <div className='text-xl flex-col gap-3'>
          <div className='' />

          {/* Illustration - same position as V1 */}
          {referralMessageId ? (
            <img
              src={il2}
              className='w-[45%] max-w-[320px] sm:w-[38%] lg:w-[28%] lg:max-w-[280px]'
              alt='Healthcare Worker'
            />
          ) : (
            <div className='flex-center'>
              <img
                src={il1}
                className='w-[70%] max-w-[520px] sm:w-[55%] lg:w-[45%] lg:max-w-[440px]'
                alt='Doctor Patient'
              />
            </div>
          )}

          <div className='h-1' />

          <h4 className='font-bold sm'>{titleLabel || slt({ key: titleKey })}</h4>
          <p className='md text-light'>{subtitleLabel || slt({ key: subtitleKey })}</p>
        </div>

        {/* Terms Checkbox - matching V1 styling */}
        <div className='form-control'>
          <label className='flex cursor-pointer label gap-3 items-start'>
            <input
              type='checkbox'
              checked={reportState.acceptedTerms || false}
              className='checkbox checkbox-primary checkbox-lg'
              onChange={(e) => handleTermsChange(e.target.checked)}
            />
            <div className='flex-1 flex-center-y h-full'>
              <span className='label-text text-light text-base'>
                {slt({
                  key: 'agree-msg',
                  vars: [
                    <a
                      key='v1'
                      href={config.termsOfService}
                      target='_blank'
                      rel='noreferrer'
                      className='link whitespace-nowrap'
                    >
                      {slt({ key: 'terms-of-Use' })}
                    </a>,
                    <a
                      key='v2'
                      href={config.privacyPolicy}
                      target='_blank'
                      rel='noreferrer'
                      className='link whitespace-nowrap'
                    >
                      {slt({ key: 'privacy-policy' })}
                    </a>,
                  ],
                })}
              </span>
            </div>
          </label>
        </div>

        {/* Start Survey Button - matching V1 styling */}
        <div className='flex-col gap-2'>
          <button
            className={classNames('btn btn-lg btn-primary', !isValid && 'btn-disabled')}
            onClick={handleStartSurvey}
          >
            {sltStr({ key: 'btn-start', upper: true })}
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
}

/**
 * Main Page Component
 * Wraps content with Container.Provider
 */
function PaSurveyHomePage() {
  return (
    <SurveyContainer.Provider>
      <PaSurveyHomePageContent />
    </SurveyContainer.Provider>
  );
}

export default memo(PaSurveyHomePage);
